import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Store, Star, Download, Search, X, ChevronDown, Send, Package } from 'lucide-react';

const ACCENT = '#f97316';

interface MarketplaceItem {
  id: string;
  item_type: 'template' | 'connector' | 'transform';
  name: string;
  description: string;
  long_description: string;
  category: string;
  tags: string[];
  author_name: string;
  version: string;
  status: string;
  price_type: 'free' | 'premium';
  price_credits?: number;
  install_count: number;
  rating_sum: number;
  rating_count: number;
  screenshots: string[];
  changelog: { version: string; date: string; notes: string }[];
  created_at: string;
  published_at: string | null;
}

interface MarketplaceReview {
  id: string;
  item_id: string;
  author_name: string;
  rating: number;
  comment: string;
  created_at: string;
}

const TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'template', label: 'Templates' },
  { value: 'connector', label: 'Connectors' },
  { value: 'transform', label: 'Transforms' },
];

const SORT_OPTIONS = [
  { value: 'installs', label: 'Most Popular' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest' },
];

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  template: { bg: '#dbeafe', color: '#1e40af' },
  connector: { bg: '#dcfce7', color: '#166534' },
  transform: { bg: '#fef3c7', color: '#92400e' },
};

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={i <= rating ? '#f59e0b' : 'none'} stroke={i <= rating ? '#f59e0b' : '#d1d5db'} strokeWidth={2}>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

function InteractiveStarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <span style={{ display: 'inline-flex', gap: 2, cursor: 'pointer' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg
          key={i}
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill={i <= (hover || value) ? '#f59e0b' : 'none'}
          stroke={i <= (hover || value) ? '#f59e0b' : '#d1d5db'}
          strokeWidth={2}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          style={{ cursor: 'pointer' }}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

export default function MarketplacePage() {
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [itemType, setItemType] = useState('all');
  const [sort, setSort] = useState('installs');
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);
  const [reviews, setReviews] = useState<MarketplaceReview[]>([]);
  const [installing, setInstalling] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (itemType !== 'all') params.set('item_type', itemType);
      if (search) params.set('search', search);
      params.set('sort', sort);
      const data = await api.get<{ items: MarketplaceItem[] }>(`/marketplace?${params}`);
      setItems(data.items);
    } catch { /* empty */ }
    setLoading(false);
  }, [itemType, search, sort]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const openDetail = async (item: MarketplaceItem) => {
    setSelectedItem(item);
    try {
      const data = await api.get<{ item: MarketplaceItem; reviews: MarketplaceReview[] }>(`/marketplace/${item.id}`);
      setSelectedItem(data.item);
      setReviews(data.reviews);
    } catch { /* empty */ }
  };

  const handleInstall = async () => {
    if (!selectedItem) return;
    setInstalling(true);
    try {
      await api.post(`/marketplace/${selectedItem.id}/install`, { variables: {} });
      setSelectedItem({ ...selectedItem, install_count: selectedItem.install_count + 1 });
      loadItems();
    } catch { /* empty */ }
    setInstalling(false);
  };

  const handleReview = async () => {
    if (!selectedItem || reviewRating === 0 || !reviewComment.trim()) return;
    setSubmittingReview(true);
    try {
      const data = await api.post<{ review: MarketplaceReview }>(`/marketplace/${selectedItem.id}/review`, {
        rating: reviewRating,
        comment: reviewComment.trim(),
      });
      setReviews([data.review, ...reviews]);
      setSelectedItem({
        ...selectedItem,
        rating_sum: selectedItem.rating_sum + reviewRating,
        rating_count: selectedItem.rating_count + 1,
      });
      setReviewRating(0);
      setReviewComment('');
    } catch { /* empty */ }
    setSubmittingReview(false);
  };

  const avgRating = (item: MarketplaceItem) =>
    item.rating_count > 0 ? Math.round(item.rating_sum / item.rating_count) : 0;

  return (
    <div>
      {/* Hero section */}
      <div style={{
        background: `linear-gradient(135deg, ${ACCENT}, #ea580c)`,
        borderRadius: '0.75rem',
        padding: '2.5rem 2rem',
        marginBottom: '1.5rem',
        color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Store size={32} />
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700 }}>Bridge Marketplace</h1>
        </div>
        <p style={{ margin: '0.5rem 0 1.5rem', opacity: 0.9, fontSize: '1rem' }}>
          Discover templates, connectors, and transforms built by the community
        </p>
        <div style={{ position: 'relative', maxWidth: 600 }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: 11, color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search marketplace..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem 0.75rem 0.625rem 2.25rem',
              borderRadius: '0.5rem',
              border: 'none',
              fontSize: '0.9375rem',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Filters row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        {/* Type tabs */}
        <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: '#f3f4f6', borderRadius: '0.5rem', padding: '0.25rem' }}>
          {TYPE_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setItemType(tab.value)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: itemType === tab.value ? 600 : 400,
                backgroundColor: itemType === tab.value ? '#fff' : 'transparent',
                color: itemType === tab.value ? '#111827' : '#6b7280',
                boxShadow: itemType === tab.value ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sort dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
              border: '1px solid #d1d5db', backgroundColor: '#fff',
              cursor: 'pointer', fontSize: '0.875rem', color: '#374151',
            }}
          >
            {SORT_OPTIONS.find(s => s.value === sort)?.label}
            <ChevronDown size={14} />
          </button>
          {showSortDropdown && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4,
              backgroundColor: '#fff', borderRadius: '0.375rem', border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px rgba(0,0,0,0.07)', zIndex: 10, minWidth: 160,
            }}>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setSort(opt.value); setShowSortDropdown(false); }}
                  style={{
                    display: 'block', width: '100%', padding: '0.5rem 0.75rem',
                    border: 'none', backgroundColor: sort === opt.value ? '#f3f4f6' : '#fff',
                    cursor: 'pointer', fontSize: '0.875rem', textAlign: 'left',
                    color: sort === opt.value ? ACCENT : '#374151',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>Loading marketplace...</div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          <Package size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
          <p style={{ fontSize: '1rem', fontWeight: 500, color: '#6b7280' }}>No items found</p>
          <p style={{ fontSize: '0.875rem' }}>Try adjusting your search or filters</p>
        </div>
      )}

      {/* Grid of cards */}
      {!loading && items.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
        }}>
          {items.map(item => {
            const avg = avgRating(item);
            const typeColor = TYPE_COLORS[item.item_type] || TYPE_COLORS.template;
            return (
              <div
                key={item.id}
                onClick={() => openDetail(item)}
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e7eb',
                  padding: '1.25rem',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.625rem',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>{item.name}</h3>
                  <span style={{
                    padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem',
                    fontWeight: 600, textTransform: 'uppercase',
                    backgroundColor: item.price_type === 'free' ? '#dcfce7' : '#fef3c7',
                    color: item.price_type === 'free' ? '#166534' : '#92400e',
                  }}>
                    {item.price_type === 'free' ? 'Free' : `${item.price_credits} credits`}
                  </span>
                </div>

                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.4 }}>
                  by {item.author_name}
                </p>

                <p style={{
                  margin: 0, fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.5,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {item.description}
                </p>

                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.6875rem',
                    fontWeight: 500, backgroundColor: typeColor.bg, color: typeColor.color,
                  }}>
                    {item.item_type}
                  </span>
                  {item.tags.slice(0, 2).map(tag => (
                    <span key={tag} style={{
                      padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.6875rem',
                      backgroundColor: '#f3f4f6', color: '#6b7280',
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <StarRating rating={avg} />
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>({item.rating_count})</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                    <Download size={12} />
                    {item.install_count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Publish CTA */}
      <div style={{
        marginTop: '2rem',
        padding: '2rem',
        backgroundColor: '#fff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        textAlign: 'center',
      }}>
        <Send size={32} style={{ color: ACCENT, margin: '0 auto 0.75rem' }} />
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>Publish Your Own</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
          Share your flows with the Bridge community. Go to any flow and publish it to the marketplace.
        </p>
      </div>

      {/* Detail modal */}
      {selectedItem && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            paddingTop: '5vh', zIndex: 100, overflow: 'auto',
          }}
          onClick={e => { if (e.target === e.currentTarget) { setSelectedItem(null); setReviews([]); setReviewRating(0); setReviewComment(''); } }}
        >
          <div style={{
            backgroundColor: '#fff', borderRadius: '0.75rem', width: '100%', maxWidth: 720,
            maxHeight: '85vh', overflow: 'auto', padding: '2rem', position: 'relative',
          }}>
            {/* Close button */}
            <button
              onClick={() => { setSelectedItem(null); setReviews([]); setReviewRating(0); setReviewComment(''); }}
              style={{
                position: 'absolute', top: 16, right: 16, border: 'none',
                backgroundColor: 'transparent', cursor: 'pointer', color: '#9ca3af',
              }}
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.375rem', fontWeight: 700 }}>{selectedItem.name}</h2>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                  by {selectedItem.author_name} &middot; v{selectedItem.version}
                </p>
              </div>
              <span style={{
                padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem',
                fontWeight: 600, textTransform: 'uppercase',
                backgroundColor: selectedItem.price_type === 'free' ? '#dcfce7' : '#fef3c7',
                color: selectedItem.price_type === 'free' ? '#166534' : '#92400e',
              }}>
                {selectedItem.price_type === 'free' ? 'Free' : `${selectedItem.price_credits} credits`}
              </span>
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <StarRating rating={avgRating(selectedItem)} />
                <span>({selectedItem.rating_count} reviews)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Download size={14} />
                {selectedItem.install_count} installs
              </div>
            </div>

            {/* Install button */}
            <button
              onClick={handleInstall}
              disabled={installing}
              style={{
                padding: '0.625rem 1.5rem', backgroundColor: ACCENT, color: '#fff',
                border: 'none', borderRadius: '0.375rem', fontWeight: 600,
                fontSize: '0.875rem', cursor: installing ? 'not-allowed' : 'pointer',
                opacity: installing ? 0.6 : 1, marginBottom: '1.5rem',
              }}
            >
              {installing ? 'Installing...' : 'Install'}
            </button>

            {/* Description */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600 }}>Description</h4>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {selectedItem.long_description || selectedItem.description}
              </p>
            </div>

            {/* Tags */}
            {selectedItem.tags.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600 }}>Tags</h4>
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  {selectedItem.tags.map(tag => (
                    <span key={tag} style={{
                      padding: '0.25rem 0.625rem', borderRadius: '0.25rem', fontSize: '0.75rem',
                      backgroundColor: '#f3f4f6', color: '#6b7280',
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Changelog */}
            {selectedItem.changelog.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600 }}>Changelog</h4>
                {selectedItem.changelog.map((entry, i) => (
                  <div key={i} style={{ padding: '0.5rem 0', borderBottom: i < selectedItem.changelog.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8125rem' }}>
                      <span style={{ fontWeight: 600, color: '#111827' }}>v{entry.version}</span>
                      <span style={{ color: '#9ca3af' }}>{entry.date}</span>
                    </div>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: '#4b5563' }}>{entry.notes}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reviews */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', fontWeight: 600 }}>
                Reviews ({reviews.length})
              </h4>

              {/* Star breakdown */}
              {selectedItem.rating_count > 0 && (
                <div style={{ marginBottom: '1rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = reviews.filter(r => r.rating === star).length;
                    const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                    return (
                      <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ width: 20, textAlign: 'right' }}>{star}</span>
                        <Star size={12} fill="#f59e0b" stroke="#f59e0b" />
                        <div style={{ flex: 1, height: 6, backgroundColor: '#f3f4f6', borderRadius: 3 }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#f59e0b', borderRadius: 3 }} />
                        </div>
                        <span style={{ width: 24, textAlign: 'right' }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Review list */}
              {reviews.map(review => (
                <div key={review.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{review.author_name}</span>
                      <StarRating rating={review.rating} size={12} />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: '#4b5563' }}>{review.comment}</p>
                </div>
              ))}

              {reviews.length === 0 && (
                <p style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>No reviews yet. Be the first!</p>
              )}
            </div>

            {/* Write review */}
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '0.5rem', padding: '1rem' }}>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', fontWeight: 600 }}>Write a Review</h4>
              <div style={{ marginBottom: '0.75rem' }}>
                <InteractiveStarRating value={reviewRating} onChange={setReviewRating} />
              </div>
              <textarea
                placeholder="Share your experience..."
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                rows={3}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  border: '1px solid #d1d5db', fontSize: '0.875rem', resize: 'vertical',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={handleReview}
                disabled={submittingReview || reviewRating === 0 || !reviewComment.trim()}
                style={{
                  marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: ACCENT,
                  color: '#fff', border: 'none', borderRadius: '0.375rem', fontWeight: 600,
                  fontSize: '0.8125rem', cursor: submittingReview ? 'not-allowed' : 'pointer',
                  opacity: (submittingReview || reviewRating === 0 || !reviewComment.trim()) ? 0.5 : 1,
                }}
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
